import { useMutation } from "@apollo/client";
import { Question } from "phosphor-react";
import React, { useEffect, useState } from "react";
import { ButtonGroup, Header, SkipButton, SlideLayout, Text } from "../../shared-components";
import { setError } from "../../utils/setError";
import { SetOnboardingEventFlexibilityDocument } from "./__generated__/FlexMeetingSlide.generated";
import { FlexMeetingColumns } from "./components/FlexMeetingColumns";
import { FlexMeetingSlideTooltip, TooltipContent } from "./components/Tooltip";
import {
  FlexMeeting,
  FlexMeetingEventMap,
  getEventListFromFlexMeetingMap,
} from "./utils/suggestion-utils";

import { TrackingEvents, track } from "../../../../util/analytics.util";
import { Countdown } from "./components/Countdown";

export const FlexMeetingSlide = ({
  orgId,
  eventMap,
  showSecondaryButton,
  onClickPrimaryButton,
  onClickSecondaryButton,
  canSetDisabled,
}: {
  orgId: string;
  showSecondaryButton: boolean;
  onClickPrimaryButton: () => void;
  onClickSecondaryButton: () => void;
  eventMap: FlexMeetingEventMap;
  canSetDisabled: boolean;
}) => {
  const [setFlexibility] = useMutation(SetOnboardingEventFlexibilityDocument);
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  const [flexMeetingEventMap, setFlexMeetingEventMap] = useState<FlexMeetingEventMap>({});
  const [disableButtons, setDisableButtons] = useState(false);
  const flexMeetingList = getEventListFromFlexMeetingMap(flexMeetingEventMap);

  useEffect(() => {
    setFlexMeetingEventMap({ ...eventMap });
  }, [eventMap]);

  const toggleTooltip = (toggled: boolean) => {
    setShouldShowTooltip(toggled);
  };

  const updateFlexibility = (eventId: string, isFlexible: boolean) => {
    const eventToUpdate = { ...flexMeetingEventMap[eventId] };
    eventToUpdate.flexible = isFlexible;
    setFlexMeetingEventMap({ ...flexMeetingEventMap, [eventId]: eventToUpdate });
  };

  const onSaveAndMoveForward = () => {
    const eventsToEnable = flexMeetingList.reduce((idList: string[], current: FlexMeeting) => {
      if (current.flexible) {
        idList.push(current.id);
      }
      return idList;
    }, []);

    // only explicitly disable events that are marked as flexible via eventMap
    const eventsToDisable = canSetDisabled
      ? flexMeetingList.reduce((idList: string[], current: FlexMeeting) => {
          if (!current.flexible && eventMap[current.id].flexible === true) {
            idList.push(current.id);
          }
          return idList;
        }, [])
      : [];

    if (eventsToEnable?.length > 0) {
      track(TrackingEvents.WEB_ONBOARDING.FLEX_MEETING_ENABLED, {
        enabledCount: eventsToEnable.length,
      });
      setDisableButtons(true);
      void setFlexibility({
        variables: { input: { orgId, eventsToEnable, eventsToDisable } },
        onCompleted: () => {
          onClickPrimaryButton();
          setDisableButtons(false);
        },
        onError: (error) => {
          setDisableButtons(false);
          setError({
            message: "failed to make meetings flexible",
            error: error,
            showUserMessage: true,
          });
          onClickPrimaryButton();
        },
      });
    } else {
      track(TrackingEvents.WEB_ONBOARDING.FLEX_MEETING_ENABLED, {
        enabledCount: eventsToEnable?.length,
      });
      onClickPrimaryButton();
    }
  };

  const onSkip = () => {
    track(TrackingEvents.WEB_ONBOARDING.FLEX_MEETING_SKIPPED);
    onClickPrimaryButton();
  };

  return (
    <SlideLayout>
      <Header> Make these meetings flexible </Header>
      <Text>
        <div className="cw-flex cw-items-center cw-justify-center">
          <FlexMeetingSlideTooltip
            title={<TooltipContent />}
            open={shouldShowTooltip}
            onClose={() => toggleTooltip(false)}
            onOpen={() => toggleTooltip(true)}
            onClick={() => toggleTooltip(!shouldShowTooltip)}
          >
            <div className="cw-h-6 cw-w-6 cw-mr-2">
              <Question />
            </div>
          </FlexMeetingSlideTooltip>
          How will this affect meeting attendees?
        </div>
      </Text>
      <FlexMeetingColumns flexMeetings={flexMeetingList} updateFlexibility={updateFlexibility} />
      <Countdown orgId={orgId} />
      <ButtonGroup
        shouldShowSecondary={showSecondaryButton}
        primaryLabel={"Confirm"}
        secondaryLabel={"Back"}
        onClickPrimary={onSaveAndMoveForward}
        onClickSecondary={onClickSecondaryButton}
        disabled={disableButtons}
      />
      <SkipButton onSkip={onSkip}>Skip </SkipButton>
    </SlideLayout>
  );
};
